import React, { useState, useEffect, useCallback, useId } from 'react';
import { Captions, Copy, Check, ChevronDown } from 'lucide-react';
import { getVideoTranscript } from '../../services/videoActivationAPI';
import './VideoTranscript.css';

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * VideoTranscript — collapsible "Read what was said" disclosure bonded to a video/audio item.
 *
 * Props:
 *   mediaFileId  — UUID of the MediaFile
 */
function VideoTranscript({ mediaFileId }) {
  const [data, setData] = useState(null);
  const [txStatus, setTxStatus] = useState(null); // 'complete' | 'transcribing' | 'failed' | 'skipped' | null
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const panelId = useId();

  const fetchTranscript = useCallback(() => {
    if (!mediaFileId) return;
    getVideoTranscript(mediaFileId)
      .then(resp => {
        setTxStatus(resp.status);
        if (resp.status === 'complete' && resp.cues) setData(resp);
      })
      .catch(() => setTxStatus(null))
      .finally(() => setLoading(false));
  }, [mediaFileId]);

  useEffect(() => { fetchTranscript(); }, [fetchTranscript]);

  // Poll while transcribing
  useEffect(() => {
    if (txStatus !== 'transcribing') return;
    const interval = setInterval(fetchTranscript, 5000);
    return () => clearInterval(interval);
  }, [txStatus, fetchTranscript]);

  if (loading) return null;
  // Hide for: no data, not processed, not found, skipped (no speech), failed
  if (!txStatus || txStatus === 'not_processed' || txStatus === 'not_found' || txStatus === 'skipped' || txStatus === 'failed') return null;

  const cues = data?.cues || [];
  const showSpeakers = data?.speakers && Object.keys(data.speakers).length >= 2;

  const plainText = cues.map(c => {
    const spkName = showSpeakers && data.speakers[c.spk] ? data.speakers[c.spk].name + ': ' : '';
    return `[${fmt(c.start)}] ${spkName}${c.text}`;
  }).join('\n');

  const copyAll = () => {
    try { navigator.clipboard.writeText(plainText); } catch (e) { /* fallback */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isProcessing = txStatus === 'transcribing';
  const isReady = txStatus === 'complete' && cues.length > 0;

  // Processing state — disabled header
  if (isProcessing) {
    return (
      <div className="vt-disclosure" style={{ opacity: 0.7 }}>
        <div className="vt-disclosure-header" style={{ cursor: 'default' }}>
          <Captions size={17} color="#556B2F" style={{ flexShrink: 0 }} />
          <span className="vt-disclosure-label">Transcript is being prepared...</span>
          <span className="vt-disclosure-meta">
            <span className="vt-dot-pulse" /> Processing
          </span>
        </div>
      </div>
    );
  }

  if (!isReady) return null;

  return (
    <div className="vt-disclosure">
      <button
        className="vt-disclosure-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        <Captions size={17} color="#556B2F" style={{ flexShrink: 0 }} />
        <span className="vt-disclosure-label">Read what was said</span>
        <span className="vt-disclosure-meta">Auto-generated</span>
        <ChevronDown
          size={18}
          color="#9AA088"
          className={`vt-disclosure-chevron${expanded ? ' open' : ''}`}
        />
      </button>

      <div
        id={panelId}
        className={`vt-disclosure-panel${expanded ? ' expanded' : ''}`}
        role="region"
      >
        <div className="vt-disclosure-body">
          {cues.map((c, i) => {
            const sp = showSpeakers && data.speakers[c.spk];
            return (
              <p key={i} className="vt-disclosure-line">
                {sp && <span className="vt-disclosure-speaker">{sp.name}:</span>}
                {' '}{c.text}
              </p>
            );
          })}
        </div>
        <div className="vt-disclosure-footer">
          <button className={`vt-disclosure-copy${copied ? ' copied' : ''}`} onClick={copyAll}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default VideoTranscript;
